/**
 * Factory Control — cross-factory overview landing page (`/factory/control`).
 *
 * Gộp 2 thứ vào 1 trang (mirror Access `frmFactorySummaryMain`):
 *   ① Bảng hợp nhất TẤT CẢ factory — "Coffee stocks by qualities" (Access mode
 *      `store_char='ALL'` → view_factory_summary_today_all). Web dựng client-side
 *      bằng cách gom byQuality của từng factory theo quality_code (KHÔNG cần
 *      endpoint mới — tái dùng `GET /factories/{code}/summary` các card đã fetch).
 *   ② Card mỗi factory với KPI tồn kho, click → mở summary chi tiết `/<code>`.
 *
 * Per-factory dashboard chi tiết vẫn ở `/<factoryCode>/` (tab SUMMARY). KPI &
 * TỔNG tính khớp FactorySummary footer (Inventory net = Σ txt_exnet_weight2 + WIP).
 * Fail-loud per-factory: 1 factory lỗi không kéo sập bảng/trang. [[no-silent-tenant-default]]
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import type { Factory } from '@/config/factories'
import { useAppSelector } from '@/store'
import PageHeader from '@/components/PageHeader'
import {
  useListFactoriesQuery,
  useGetFactorySummaryQuery,
  type FactoryCell,
  type FactorySummaryResponse,
} from '@/store/api/factoryApi'
import {
  FuturesReportTable,
  displayAccessInteger,
  type ColumnDef,
  type ReportRow,
} from '../futures/FuturesReportTable'
import { BlockTitle } from './maps/BlockTitle'
import { RPT } from '../dashboard/cashflows/reportTokens'

/* ── Column meta khớp FactorySummary (Access lb2..lb5 groups). ────────────── */
const QUALITY_COLUMN_META: Record<string, { headerName: string; group?: string }> = {
  quality_code: { headerName: 'Quality' },
  description: { headerName: 'Spec' },
  txt_net_weight: { headerName: 'Net weight', group: 'Coffee receipt' },
  txt_base_weight: { headerName: 'Basis weight', group: 'Coffee receipt' },
  txt_net_weight2: { headerName: 'Net weight', group: 'Issue to production' },
  txt_base_weight2: { headerName: 'Basis weight', group: 'Issue to production' },
  txt_net_weight3: { headerName: 'Net weight', group: 'OUT OF FACTORY' },
  txt_exnet_weight: { headerName: 'Basis weight', group: 'OUT OF FACTORY' },
  txt_exnet_weight2: { headerName: 'Net weight', group: 'Inventory' },
  txtex_base_weight: { headerName: 'Basis weight', group: 'Inventory' },
}

/** factory_code → permKey (khớp navigation.ts mục Factory). Factory nào không
 *  có quyền View → ẩn card + KHÔNG fetch + KHÔNG gộp vào bảng ALL. */
const FACTORY_PERM_KEY: Record<string, string> = {
  baoloc: 'factoryBl',
  nhontrach: 'factoryNt',
  nguyencuong: 'factoryNc',
  nongsan: 'factoryNs',
  pacorini: 'factoryPaco',
}

/** Thứ tự card = thứ tự nav menu (navigation.ts mục Factory). Factory ngoài
 *  list (mới thêm) xếp cuối, giữ thứ tự API. */
const FACTORY_NAV_ORDER = ['baoloc', 'nhontrach', 'nguyencuong', 'nongsan', 'pacorini']
const navRank = (code: string): number => {
  const i = FACTORY_NAV_ORDER.indexOf(code)
  return i === -1 ? FACTORY_NAV_ORDER.length : i
}

function humanizeFieldKey(key: string): string {
  return key
    .replace(/^txt_/i, '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

interface FactoryKpis {
  received: number
  issued: number
  inventory: number
  wip: number
}

function sumQualityColumn(
  rows: Array<Record<string, FactoryCell>> | undefined,
  key: string,
): number {
  if (!rows?.length) return 0
  let sum = 0
  for (const r of rows) {
    const v = r[key]
    if (typeof v === 'number' && Number.isFinite(v)) sum += v
  }
  return sum
}

function wipOf(data: FactorySummaryResponse): number {
  const raw = data.sumRow?.txt_unfinished
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 0
}

const KPI_DEFS: Array<{ key: keyof FactoryKpis; label: string }> = [
  { key: 'inventory', label: 'Inventory · net weight' },
  { key: 'received', label: 'Coffee receipt' },
  { key: 'issued', label: 'Issued to production' },
  { key: 'wip', label: 'Work-in-progress' },
]

/* ── Một card / factory: tự fetch summary, tính KPI, lift data lên parent ──── */
function FactorySummaryCard({
  factory,
  canView,
  onData,
}: {
  factory: Factory
  /** User có quyền View/Edit factory này không. False → card khóa, không fetch. */
  canView: boolean
  onData: (code: string, data: FactorySummaryResponse | null) => void
}) {
  const navigate = useNavigate()
  // Không có quyền → skip query (không fetch data factory bị cấm).
  const { data, isFetching, isError } = useGetFactorySummaryQuery(factory.factoryCode, {
    skip: !canView,
    refetchOnMountOrArgChange: true,
  })

  // Lift summary lên parent để dựng bảng hợp nhất. data ổn định theo RTK cache
  // (chỉ đổi reference khi fetch xong) → không loop. Card khóa luôn lift null →
  // KHÔNG gộp vào bảng ALL.
  useEffect(() => {
    onData(factory.factoryCode, canView ? (data ?? null) : null)
  }, [factory.factoryCode, canView, data, onData])

  const kpis: FactoryKpis | null = data
    ? {
        received: sumQualityColumn(data.byQuality, 'txt_net_weight'),
        issued: sumQualityColumn(data.byQuality, 'txt_net_weight2'),
        inventory: sumQualityColumn(data.byQuality, 'txt_exnet_weight2') + wipOf(data),
        wip: wipOf(data),
      }
    : null

  const open = () => navigate(`/${factory.factoryCode}`)

  return (
    <Paper
      elevation={0}
      // Card khóa (no permission) → không click, không focus-as-button, cursor default.
      onClick={canView ? open : undefined}
      role={canView ? 'button' : undefined}
      tabIndex={canView ? 0 : undefined}
      onKeyDown={
        canView
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                open()
              }
            }
          : undefined
      }
      sx={{
        border: `1px solid ${RPT.border}`,
        borderRadius: '10px',
        bgcolor: '#fff',
        overflow: 'hidden',
        cursor: canView ? 'pointer' : 'default',
        transition: 'box-shadow 120ms, border-color 120ms',
        ...(canView
          ? {
              '&:hover': { borderColor: RPT.forest, boxShadow: '0 2px 10px rgba(11,54,51,0.12)' },
              '&:focus-visible': { outline: `2px solid ${RPT.forest}`, outlineOffset: 2 },
            }
          : { opacity: 0.85 }),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 1, bgcolor: canView ? RPT.forest : RPT.sub, color: '#fff' }}
      >
        <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '0.9rem' }} noWrap>
          {factory.factoryName}
        </Typography>
        {canView ? <ArrowForwardIcon sx={{ fontSize: '1.05rem', opacity: 0.85 }} /> : null}
      </Stack>

      <Box sx={{ p: 1.5 }}>
        {!canView ? (
          <Alert severity="warning" sx={{ py: 0.25 }}>
            You don't have permission to view this factory.
          </Alert>
        ) : isError ? (
          <Alert severity="error" sx={{ py: 0.25 }}>
            Could not load summary.
          </Alert>
        ) : isFetching && !data ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 2.5 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {KPI_DEFS.map(({ key, label }) => (
              <Box key={key}>
                <Typography sx={{ fontSize: '0.62rem', color: RPT.sub, lineHeight: 1.2 }}>
                  {label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: key === 'inventory' ? RPT.forest : RPT.ink,
                    lineHeight: 1.3,
                  }}
                >
                  {kpis ? displayAccessInteger(kpis[key]) : '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  )
}

/* ── Bảng hợp nhất ALL factory — gom byQuality theo quality_code. ──────────── */
function ConsolidatedQualityTable({
  dataMap,
}: {
  dataMap: Record<string, FactorySummaryResponse | null>
}) {
  const datas = useMemo(
    () => Object.values(dataMap).filter((d): d is FactorySummaryResponse => d != null),
    [dataMap],
  )

  // Columns derive từ sample factory đầu tiên có byQuality (giống FactorySummary).
  const columns = useMemo<ColumnDef[]>(() => {
    const sample = datas.find((d) => d.byQuality?.length)?.byQuality?.[0]
    if (!sample) return []
    return Object.keys(sample)
      .filter((k) => k !== 'id')
      .map((field) => {
        const meta = QUALITY_COLUMN_META[field.toLowerCase()]
        const numeric = typeof sample[field] === 'number'
        const minWidth = field === 'quality_code' ? 110 : field === 'description' ? 200 : 80
        return {
          key: field,
          label: meta?.headerName ?? humanizeFieldKey(field),
          group: meta?.group,
          numeric,
          minWidth,
        }
      })
  }, [datas])

  // Aggregate: group theo quality_code, cộng các cột numeric; description giữ
  // giá trị non-empty đầu tiên gặp.
  const { rows, wip } = useMemo(() => {
    const numericKeys = columns.filter((c) => c.numeric).map((c) => c.key)
    const agg = new Map<string, ReportRow>()
    let order = 0
    const seqOf = new Map<string, number>()
    let wipSum = 0
    for (const d of datas) {
      wipSum += wipOf(d)
      for (const r of d.byQuality ?? []) {
        const code = String(r.quality_code ?? '').trim()
        if (!code) continue
        let bucket = agg.get(code)
        if (!bucket) {
          bucket = { quality_code: code }
          agg.set(code, bucket)
          seqOf.set(code, order++)
        }
        const desc = r.description
        if (
          (bucket.description == null || bucket.description === '') &&
          typeof desc === 'string' &&
          desc.trim()
        ) {
          bucket.description = desc
        }
        for (const k of numericKeys) {
          const v = r[k]
          if (typeof v === 'number' && Number.isFinite(v)) {
            bucket[k] = ((bucket[k] as number) ?? 0) + v
          }
        }
      }
    }
    const sorted = [...agg.values()].sort(
      (a, b) => (seqOf.get(String(a.quality_code)) ?? 0) - (seqOf.get(String(b.quality_code)) ?? 0),
    )
    return { rows: sorted, wip: wipSum }
  }, [datas, columns])

  // Footer: WORK-IN-PROGRESS + TOTAL (khớp FactorySummary footer).
  const reportRows = useMemo<ReportRow[]>(() => {
    if (!rows.length || !columns.length) return rows
    const labelCol =
      columns.find((c) => c.key.toLowerCase() === 'description') ?? columns.find((c) => !c.numeric)
    const invKey = 'txt_exnet_weight2'

    const wipRow: ReportRow = { fullSymbol: 'subtotal' }
    if (labelCol) wipRow[labelCol.key] = 'WORK-IN-PROGRESS'
    if (columns.some((c) => c.key === invKey)) wipRow[invKey] = wip

    const totalRow: ReportRow = { fullSymbol: 'total' }
    if (labelCol) totalRow[labelCol.key] = 'TOTAL'
    for (const c of columns) {
      if (!c.numeric) continue
      let sum = 0
      let has = false
      for (const r of rows) {
        const v = r[c.key]
        if (typeof v === 'number' && Number.isFinite(v)) {
          sum += v
          has = true
        }
      }
      totalRow[c.key] = has ? sum : null
    }
    // Inventory total gồm cả WIP.
    if (columns.some((c) => c.key === invKey)) {
      const cur = totalRow[invKey]
      totalRow[invKey] = (typeof cur === 'number' && Number.isFinite(cur) ? cur : 0) + wip
    }
    return [...rows, wipRow, totalRow]
  }, [rows, columns, wip])

  return (
    <Paper
      elevation={0}
      sx={{ p: 1, border: `1px solid ${RPT.border}`, borderRadius: '8px', bgcolor: '#fff', minWidth: 0 }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <BlockTitle title="Coffee stocks by qualities" />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.66rem', color: RPT.gold, fontWeight: 600 }}>
          All factories
        </Typography>
      </Stack>
      {!columns.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <FuturesReportTable
          columns={columns}
          rows={reportRows}
          density="compact"
          pinTotalRow
          numericFormatter={displayAccessInteger}
          // Auto height: lấp đầy màn còn lại, co theo 100vh nên responsive màn nhỏ.
          scrollMaxHeight="calc(100vh - 320px)"
          horizontalScroll
          freezeTitleColumns
          skin="report"
          cellFontSize={{ head: '0.66rem', body: '0.68rem' }}
        />
      )}
    </Paper>
  )
}

export default function FactorySummaryOverviewPage() {
  const { data: factories, isLoading, isError } = useListFactoriesQuery()
  const permissions = useAppSelector((s) => s.auth.permissions)
  const [dataMap, setDataMap] = useState<Record<string, FactorySummaryResponse | null>>({})

  // Hiện TẤT CẢ factory (sort theo nav order). Card không-quyền vẫn giữ nhưng
  // bị khóa (xem `canViewFactory`) — không fetch, không gộp vào bảng ALL.
  const sortedFactories = useMemo(() => {
    if (!factories) return []
    return factories.slice().sort((a, b) => navRank(a.factoryCode) - navRank(b.factoryCode))
  }, [factories])

  // canView per factory — mirror logic usePermission (fail-open khi perms chưa load).
  const hasLoadedPerms = Object.keys(permissions).length > 0
  const canViewFactory = useCallback(
    (code: string): boolean => {
      const key = FACTORY_PERM_KEY[code]
      if (!key || !hasLoadedPerms) return true
      const level = permissions[key]
      return level === 'View' || level === 'Edit'
    },
    [permissions, hasLoadedPerms],
  )

  const handleData = useCallback((code: string, data: FactorySummaryResponse | null) => {
    setDataMap((prev) => (prev[code] === data ? prev : { ...prev, [code]: data }))
  }, [])

  return (
    // House-style main-screen shell: PageHeader (title + module chip) trên cùng,
    // nội dung cuộn trong vùng flex:1 (khớp SaleContractPage/DeliveryOrder).
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader title="Factory Control" module="Factory" />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isError ? (
          <Alert severity="error">
            Could not load the factory list. Please try again or check the system
            connection.
          </Alert>
        ) : isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : !sortedFactories.length ? (
          <Typography variant="body2" sx={{ color: RPT.sub }}>
            No factories available.
          </Typography>
        ) : (
          <Stack spacing={2}>
          {/* Cùng format Paper + BlockTitle như block bảng bên dưới. */}
          <Paper
            elevation={0}
            sx={{
              p: 1,
              border: `1px solid ${RPT.border}`,
              borderRadius: '8px',
              bgcolor: '#fff',
              minWidth: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
              <BlockTitle title="By factory" />
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: '0.66rem', color: RPT.gold, fontWeight: 600 }}>
                All factories
              </Typography>
            </Stack>
            {/* 5 card 1 dòng ở màn lớn; thu nhỏ dần cho màn nhỏ. */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  lg: 'repeat(5, 1fr)',
                },
                gap: { xs: 1, md: 1.5 },
              }}
            >
              {sortedFactories.map((f) => (
                <FactorySummaryCard
                  key={f.factoryCode}
                  factory={f}
                  canView={canViewFactory(f.factoryCode)}
                  onData={handleData}
                />
              ))}
            </Box>
          </Paper>

          <ConsolidatedQualityTable dataMap={dataMap} />
          </Stack>
        )}
      </Box>
    </Box>
  )
}
